import { algoliasearch } from 'algoliasearch';
import { buildInitialResults } from './server-state';
import { fetchSearchPreview } from './search-preview';
import { fetchTrendingItems, lazyRecommendClient } from './trending';
/**
 * Creates the Algolia runtime for an app.
 *
 * Two client instances are created on purpose. InstantSearch's
 * `hydrateSearchClient` monkey-patches `search` on whichever client it is
 * handed, which breaks the modern `searchSingleIndex` / `searchForHits` APIs.
 * `instantSearchClient` is the one that gets patched; `directClient` stays
 * pristine and is the only one this package uses for its own requests. Neither
 * is exported, so the two can never be mixed up by a caller.
 *
 * @param config - Credentials and the logical-to-physical index map.
 * @returns The runtime handed to components and load functions.
 */
export function createAlgolia(config) {
    const { appId, searchApiKey, indices } = config;
    const instantSearchClient = algoliasearch(appId, searchApiKey);
    const directClient = algoliasearch(appId, searchApiKey);
    const recommendClient = lazyRecommendClient(directClient);
    const resolveIndex = (key) => {
        const indexName = indices[key];
        if (!indexName) {
            throw new Error(`[algolia] unknown index key "${key}" - declare it in createAlgolia({ indices }).`);
        }
        return indexName;
    };
    return {
        client: instantSearchClient,
        indices: indices,
        async fetchInitialResults(query) {
            const queries = Array.isArray(query) ? query : [query];
            const entries = await Promise.all(queries.map(async (q) => buildInitialResults(directClient, resolveIndex(q.index), q)));
            return Object.assign({}, ...entries);
        },
        async fetchTrendingItems(query) {
            return fetchTrendingItems(recommendClient(), resolveIndex(query.index), query);
        },
        async fetchCounts(query, indexKeys) {
            const { results } = await directClient.searchForHits({
                requests: indexKeys.map((key) => ({
                    indexName: resolveIndex(key),
                    query,
                    hitsPerPage: 0
                }))
            });
            const counts = {};
            indexKeys.forEach((key, i) => {
                counts[key] = results[i]?.nbHits ?? 0;
            });
            return counts;
        },
        searchPreview: ((query, options) => fetchSearchPreview(query, {
            client: directClient,
            coursesIndexName: resolveIndex('courses'),
            instructorsIndexName: resolveIndex('instructors'),
            ...options
        }))
    };
}
